import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  Input
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {HttpClient} from '@angular/common/http';
import {UserAddressOptionDto} from '../../../../models/generated-models';
import {environment} from '../../../../environments/environment';

@Component({
  selector: 'app-subdistrict-autocomplete',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './subdistrict-autocomplete.component.html',
  styleUrl: './subdistrict-autocomplete.component.scss',
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SubdistrictAutocompleteComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => SubdistrictAutocompleteComponent), multi: true}
  ]
})
export class SubdistrictAutocompleteComponent implements ControlValueAccessor, Validator {
  readonly id = input<string>('subdistrict');
  readonly name = input<string>('subdistrict');
  readonly size = input<'sm' | 'md'>('md');
  readonly disabled = input<boolean>(false);
  readonly minLength = input<number>(2);
  readonly placeholder = input<string>('พิมพ์ค้นหาชื่อตำบล/แขวง');

  @Input()
  set required(value: boolean) {
    this.requiredValue = value;
    this.validatorChange();
  }

  get required(): boolean {
    return this.requiredValue;
  }

  protected searchText = '';
  protected suggestions: UserAddressOptionDto[] = [];
  protected loading = false;
  protected open = false;
  protected formDisabled = false;
  protected readonly standaloneOptions = {standalone: true};

  private selectedValue: UserAddressOptionDto | null = null;
  private requestSequence = 0;
  private requiredValue = false;
  private readonly http = inject(HttpClient);
  private readonly host = inject(ElementRef<HTMLElement>);
  private onChange: (value: UserAddressOptionDto | null) => void = () => {
  };
  private onTouched: () => void = () => {
  };
  private validatorChange: () => void = () => {
  };

  protected get isDisabled(): boolean {
    return this.disabled() || this.formDisabled;
  }

  writeValue(value: UserAddressOptionDto | null): void {
    this.selectedValue = value ?? null;
    this.searchText = value ? this.formatSubdistrict(value) : '';
    if (!value) {
      this.suggestions = [];
    }
  }

  registerOnChange(fn: (value: UserAddressOptionDto | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.validatorChange = fn;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.required) {
      return null;
    }
    return this.selectedValue?.subdistrictId != null ? null : {required: true};
  }

  @HostListener('document:click', ['$event'])
  protected onClickOutside(event: Event): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
      this.onTouched();
    }
  }

  protected onInputChange(): void {
    this.selectedValue = null;
    this.onChange(null);

    const term = this.searchText.trim();
    if (term.length < this.minLength()) {
      this.requestSequence += 1;
      this.suggestions = [];
      this.loading = false;
      this.open = false;
      return;
    }

    this.loadSuggestions(term);
  }

  protected onFocus(): void {
    const term = this.searchText.trim();
    if (this.suggestions.length > 0) {
      // Focus can arrive synchronously in the middle of change detection
      // (programmatic .focus() calls, browser focus restoration on DOM
      // removal). Flipping `open` here throws NG0100 on [attr.aria-expanded],
      // so defer it past the current execution context and recheck.
      queueMicrotask(() => {
        if (this.suggestions.length > 0) {
          this.open = true;
        }
      });
    } else if (term.length >= this.minLength()) {
      this.loadSuggestions(term);
    }
  }

  protected selectSubdistrict(item: UserAddressOptionDto): void {
    this.selectedValue = item;
    this.searchText = this.formatSubdistrict(item);
    this.suggestions = [];
    this.open = false;
    this.onChange(item);
    this.onTouched();
  }

  protected handleBlur(): void {
    setTimeout(() => {
      this.open = false;
      this.onTouched();
    }, 150);
  }

  protected trackBySubdistrictId(index: number, item: UserAddressOptionDto): number | string {
    return item.subdistrictId ?? item.text ?? index;
  }

  protected formatOption(item: UserAddressOptionDto): string {
    const districtPrefix = item.provinceTitle === 'กรุงเทพมหานคร' ? 'เขต' : 'อ.';
    return [
      item.districtTitle ? `${districtPrefix}${item.districtTitle}` : null,
      item.provinceTitle ? `จ.${item.provinceTitle}` : null
    ].filter((part): part is string => !!part).join(' ');
  }

  private loadSuggestions(term: string): void {
    const requestId = ++this.requestSequence;
    this.loading = true;
    this.http.get<UserAddressOptionDto[]>(`${environment.serverUrl}/api/users/address-options`, {
      params: {search: term, limit: 10}
    }).subscribe({
      next: (items) => {
        if (requestId !== this.requestSequence) {
          return;
        }
        this.suggestions = items ?? [];
        this.open = this.suggestions.length > 0;
        this.loading = false;
      },
      error: () => {
        if (requestId !== this.requestSequence) {
          return;
        }
        this.suggestions = [];
        this.open = false;
        this.loading = false;
      }
    });
  }

  private formatSubdistrict(item: UserAddressOptionDto): string {
    return `${item.subdistrictTitle ?? item.text ?? ''}`.trim();
  }
}
